/* eslint-disable max-len */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Card,
  Input,
  DatePicker,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { formatReceivedValues } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import NumericInput from 'components/NumericInput';
import {
  FLOAT_NUMER_PATTERN,
  integerRegExp,
  SLUG_PATTERN,
} from 'utils/patterns';
import Select from 'components/Select';
import ModalConfirmation from 'components/ModalConfirmation';
import { getEstadosGlobales, getPeriodicidadesDePagos } from 'api/catalogos/index';
import { getPeriodosFiscales } from 'api/configuraciones/periodosFiscales';
import { getTiposDeNominasEmpleados } from 'api/nominas/catalogos/tiposNominasEmpleados';
import {
  getPeriodosDeNominas,
  postPeriodoDeNomina,
  deletePeriodoDeNomina,
  patchPeriodoDeNomina,
  getPeriodoDeNomina,
} from 'api/nominas/periodos/periodosDeNominas';
import { checkToken } from 'api/utils';
import { getModelPermission } from 'utils/permission';
import PeriodoDeNominaDetalle, { periodoDeNominaDetallePermission } from './PeriodosDeNominasDetalles';
import {
  ESTATUS_CANCELADO,
  ESTATUS_AUTORIZADO,
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
} from '../../../utils/constants';

export const periodoDeNominaPermission = {
  permissionModel: 'periododenomina',
  catalogsModels: [],
  tabsModels: [],
};

const PeriodoDeNomina = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [periodicidades, setPeriodicidades] = useState([]);
  const [nominas, setNominas] = useState([]);
  const [periodosFiscales, setPeriodosFiscales] = useState([]);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [estadosFiltered, setEstadosFiltered] = useState([]);
  const [estatusInactivo, setEstatusInactivo] = useState(false);
  const [estatusAutorizado, setEstatusAutorizado] = useState(false);
  const [confirmationVisibleEstatus, setConfirmationVisibleEstatus] = useState(false);
  const [disableAuth, setDisableAuth] = useState(false);

  const formatReceivedClone = (values) => {
    if (Array.isArray(values)) {
      let valueClone = values.map((e) => formatReceivedValues(e));
      valueClone = valueClone.map((e) => ({
        ...e,
        tipo_de_nomina_empleado_no_nomina: e?.tipo_de_nomina_empleado,
      }));
      return valueClone;
    }
    return formatReceivedValues(values);
  };

  const getPermission = getModelPermission(
    periodoDeNominaDetallePermission.permissionModel,
    periodoDeNominaDetallePermission.catalogsModels,
    periodoDeNominaDetallePermission.tabsModels,
    false,
  );

  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getPeriodosDeNominas();
    setData(formatReceivedClone(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _periodicidades,
        _nominas,
        _periodosFiscales,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getPeriodicidadesDePagos({ estados_globales: ESTATUS_AUTORIZADO }),
        getTiposDeNominasEmpleados({ estados_globales: ESTATUS_AUTORIZADO }),
        getPeriodosFiscales({ estados_globales: ESTATUS_AUTORIZADO }),
        getPeriodosDeNominas(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setPeriodicidades(_periodicidades);
        setNominas(_nominas);
        setPeriodosFiscales(_periodosFiscales);
        setData(formatReceivedClone(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setEstatusInactivo(false);
    setEstatusAutorizado(false);
    setSelectedRowKeys([]);
    setEstadosFiltered(estados);
    setVisible(false);
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _periodo = await getPeriodoDeNomina(match.id);
    if (_periodo?.estados_globales === ESTATUS_ACTIVO || _periodo?.estados_globales === ESTATUS_INACTIVO) {
      setEstadosFiltered(estados.filter((estado) => estado?.id === ESTATUS_INACTIVO || estado?.id === ESTATUS_ACTIVO));
    }
    if (_periodo?.estados_globales === ESTATUS_AUTORIZADO) {
      setEstadosFiltered(estados.filter((estado) => estado?.id === ESTATUS_AUTORIZADO));
      setEstatusAutorizado(true);
    }
    if (_periodo?.estados_globales === ESTATUS_INACTIVO) {
      setEstatusInactivo(true);
    }
    setTimeout(() => {
      if (_periodo) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceivedClone(_periodo));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = true) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      const saved = await (key ? patchPeriodoDeNomina(key, values) : postPeriodoDeNomina(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const handleOnFinishConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar una nómina ya autorizada');
        return;
      }
      const vals = {
        ...form.getFieldValue(),
        estados_globales: ESTATUS_AUTORIZADO,
      };
      const res = await patchPeriodoDeNomina(key, vals);
      if (res) {
        onSuccess(res);
        const _data = await getPeriodosDeNominas();
        setData(formatReceivedClone(_data));
        setConfirmationVisible(false);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnFinishConfirmationEstatus = async () => {
    setConfirmationVisibleEstatus(false);
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deletePeriodoDeNomina(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    const isAutorizado = record?.estados_globales === ESTATUS_AUTORIZADO;
    const isInactivo = record?.estados_globales === ESTATUS_INACTIVO;
    setDisableAuth(isAutorizado || isInactivo);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  // Cálculo automático de No. de Días Naturales
  const calcularDiasNaturales = () => {
    const fechaInicial = form.getFieldValue('fecha_inicial');
    const fechaFinal = form.getFieldValue('fecha_final_ejercicio');
    if (fechaInicial && fechaFinal) {
      const dias = fechaFinal.diff(fechaInicial, 'days') + 1;
      form.setFieldsValue({ no_de_dias_naturales: dias });
    }
  };

  const columns = [
    {
      titleText: 'Tipo de Nómina Empleado',
      dataIndex: 'tipo_de_nomina_empleado',
      key: 'tipo_de_nomina_empleado',
      width: 250,
      render: (val) => nominas.find((e) => e.id === val)?.descripcion,
    },
    {
      titleText: 'Periodicidad',
      dataIndex: 'periodicidad_de_pago',
      key: 'periodicidad_de_pago',
      width: 200,
      render: (val) => periodicidades.find((e) => e.id === val)?.descripcion,
    },
    {
      titleText: 'Fecha Inicial',
      dataIndex: 'fecha_inicial',
      key: 'fecha_inicial',
      width: 150,
      render: (val) => val?.format('DD/MM/YYYY'),
    },
    {
      titleText: 'Fecha Final',
      dataIndex: 'fecha_final_ejercicio',
      key: 'fecha_final_ejercicio',
      width: 150,
      render: (val) => val?.format('DD/MM/YYYY'),
    },
    {
      titleText: 'No. de Días',
      dataIndex: 'no_de_dias_que_suma',
      key: 'no_de_dias_que_suma',
      width: 130,
    },
    {
      titleText: 'No. de Nóminas',
      dataIndex: 'tipo_de_nomina_empleado_no_nomina',
      key: 'tipo_de_nomina_empleado_no_nomina',
      width: 200,
    },
    {
      titleText: 'Prefijo de Nómina',
      dataIndex: 'prefijo_foliador_nomina',
      key: 'prefijo_foliador_nomina',
      width: 200,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    contratacion: [requiredRule],
    estatus: [requiredRule],
    descripcion: [
      requiredRule,
      {
        pattern: SLUG_PATTERN,
        message: 'Ingrese una clave válida',
      },
    ],
    dias: [
      requiredRule,
      {
        pattern: integerRegExp,
        message: 'Ingrese un valor válido',
      },
    ],
    valor: [
      requiredRule,
      {
        pattern: FLOAT_NUMER_PATTERN,
        message: 'Ingrese un valor válido',
      },
    ],
    fecha_inicial: [
      requiredRule,
      {
        validator: async () => {
          const { periodo_fiscal, fecha_inicial } = form.getFieldsValue();
          const _periodo = periodosFiscales.find((e) => e.id === periodo_fiscal);
          if (_periodo?.fecha_inicial > fecha_inicial) {
            throw new Error('La Fecha Inicial debe ser mayor o igual a la fecha inicial del periodo fiscal');
          }
          if (fecha_inicial > _periodo?.fecha_final) {
            throw new Error('La Fecha Inicial no puede ser mayor que la fecha final del periodo fiscal');
          }
        },
      },
    ],
    fecha_final: [
      requiredRule,
      {
        validator: async () => {
          const { periodo_fiscal, fecha_final_ejercicio, fecha_inicial } = form.getFieldsValue();
          const _periodo = periodosFiscales.find((e) => e.id === periodo_fiscal);
          if (fecha_final_ejercicio?.diff(_periodo?.fecha_final, 'days') > 0) {
            throw new Error('La Fecha Final debe ser menor o igual a la fecha Final del periodo fiscal');
          }
          if (fecha_final_ejercicio < fecha_inicial) {
            throw new Error('La Fecha Final debe ser mayor o igual a la fecha Inicial');
          }
        },
      },
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!open ? (
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit: () => onClickEdit(),
              onClickDelete,
            }}
            disableDelete={
              data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_AUTORIZADO
              || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_CANCELADO
            }
            customActions={[
              {
                onClick: () => setConfirmationVisible(true),
                permission: { model: 'periododenomina', action: 'autorizar' },
                text: 'Autorizar',
                icon: <CheckCircleOutlined />,
                disabledBySelection: false,
                className: 'success',
                disabled: !selectedRowKeys?.length
                  || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_AUTORIZADO
                  || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_CANCELADO
                  || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_INACTIVO,
              },
            ]}
            permission={permission}
            allowExportPDF={false}
          />
        ) : (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Periodo de Nómina`}
            extra={(
              <FormSubmitControls
                onFinish={() => form.submit()}
                onCancel={onCancel}
                loading={loading}
                onFinishText="Guardar y Continuar"
                allowSave={!selectedRowKeys?.length || !disableAuth}
              />
            )}
            bordered={false}
          >
            <Form
              layout="vertical"
              form={form}
              onFinish={onFinish}
              scrollToFirstError
              name="general"
            >
              <Row gutter={10}>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="tipo_de_nomina_empleado"
                    label="Tipo de Nómina Empleado"
                    rules={rules.required}
                    hasFeedback
                  >
                    <Select
                      dataSource={nominas}
                      disabled={selectedRowKeys.length}
                      onChange={(value) => {
                        const _nomina = nominas.find((e) => e.id === value);
                        const _periodo = periodicidades.find((e) => e.id === _nomina?.periodicidad_de_pago);
                        form.setFieldsValue({
                          periodicidad_de_pago: _periodo?.id,
                          no_de_dias_que_suma: _periodo?.numero_de_dias,
                        });
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="periodicidad_de_pago"
                    label="Periodicidad"
                    rules={rules.required}
                    hasFeedback
                  >
                    <Select
                      dataSource={periodicidades}
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <NumericInput
                    name="no_de_dias_que_suma"
                    label="No. de Días"
                    rules={rules.dias}
                    // Ahora es editable en edición (solo disabled si está autorizado o inactivo)
                    disabled={estatusAutorizado || estatusInactivo}
                  />
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="periodo_fiscal"
                    label="Periodo Fiscal"
                    rules={rules.estatus}
                    hasFeedback
                  >
                    <Select
                      dataSource={periodosFiscales}
                      disabled={selectedRowKeys.length || estatusAutorizado || estatusInactivo}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="fecha_inicial"
                    label="Fecha Inicial"
                    rules={rules.fecha_inicial}
                    hasFeedback
                  >
                    <DatePicker
                      allowClear
                      format="DD/MM/YYYY"
                      disabled={selectedRowKeys.length}
                      onChange={calcularDiasNaturales}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="fecha_final_ejercicio"
                    label="Fecha Final"
                    rules={rules.fecha_final}
                    hasFeedback
                  >
                    <DatePicker
                      allowClear
                      format="DD/MM/YYYY"
                      disabled={selectedRowKeys.length}
                      onChange={calcularDiasNaturales}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <NumericInput
                    name="no_de_inicio_periodo"
                    label="No. de Inicio de Periodo"
                    rules={rules.dias}
                    disabled={selectedRowKeys.length || estatusAutorizado || estatusInactivo}
                  />
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="nomina_ajusta_cierre_mes"
                    label="¿La Nómina se Ajusta al Cierre de Cada Mes?"
                    hasFeedback
                    rules={rules.required}
                  >
                    <Select
                      trueFalse
                      disabled={selectedRowKeys.length || estatusAutorizado || estatusInactivo}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="ultima_nomina_ajusta_cierre_ano"
                    label="¿La Nómina se Ajusta al Cierre de Cada Año?"
                    hasFeedback
                    rules={rules.required}
                  >
                    <Select
                      trueFalse
                      disabled={selectedRowKeys.length || estatusAutorizado || estatusInactivo}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="ultima_nomina_pagada_es_cierre"
                    label="Último Periodo del Mes es de Cierre"
                    hasFeedback
                    rules={rules.required}
                  >
                    <Select
                      trueFalse
                      disabled={selectedRowKeys.length || estatusAutorizado || estatusInactivo}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="prefijo_foliador_nomina"
                    label="Prefijo de Nómina"
                    rules={rules.descripcion}
                    hasFeedback
                  >
                    <Input
                      allowClear
                      disabled={selectedRowKeys.length || estatusAutorizado || estatusInactivo}
                    />
                  </Form.Item>
                </Col>
                {selectedRowKeys.length ? (
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="estados_globales"
                      label="Estatus"
                      rules={rules.estatus}
                      hasFeedback
                    >
                      <Select
                        dataSource={estadosFiltered}
                        disabled={estatusAutorizado || estatusInactivo}
                        onChange={(val) => {
                          if (val === ESTATUS_INACTIVO) {
                            setConfirmationVisibleEstatus(true);
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                ) : (
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="estados_globales"
                      label="Estatus"
                      rules={rules.estatus}
                      hasFeedback
                      initialValue={1}
                    >
                      <Select
                        dataSource={estados}
                        disabled
                      />
                    </Form.Item>
                  </Col>
                )}
              </Row>
              {selectedRowKeys.length ? (
                <>
                  <p style={{ fontWeight: 600, marginBottom: 0, color: '#D88F4F' }}>Periodos</p>
                  <PeriodoDeNominaDetalle
                    permission={getPermission}
                    selected={selectedRowKeys[0]}
                    estatusAutorizado={estatusAutorizado}
                    estatusInactivo={estatusInactivo}
                  />
                </>
              ) : null}
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>
          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Periodo de Nómina ${form.getFieldValue('prefijo_foliador_nomina')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmation}
          ConfirmAutorize
          onCancel={() => {
            setConfirmationVisible(false);
          }}
          open={confirmationVisible}
          contentTitle={`Autorizar el Periodo ${form.getFieldValue('prefijo_foliador_nomina')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmationEstatus}
          ConfirmInactive
          onCancel={() => {
            setConfirmationVisibleEstatus(false);
            form.setFieldValue('estados_globales', 1);
          }}
          open={confirmationVisibleEstatus}
          contentTitle={`Inactivar el Periodo ${form.getFieldValue('prefijo_foliador_nomina')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default PeriodoDeNomina;
